import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import { Address } from '../domain/types';
import { userRepository } from '../data/userRepository';
import { logger } from '../infrastructure/logger';
import { useAuth } from './auth';

interface AddressContextType {
  addresses: Address[];
  loading: boolean;

  // Operations
  addAddress: (address: Omit<Address, 'id'>) => Promise<boolean>;
  removeAddress: (address: Address) => Promise<boolean>;
  setDefaultAddress: (address: Address) => Promise<boolean>;

  // Helper
  getDefaultAddress: () => Address | undefined;

  // Refresh
  refreshAddresses: (userId: string) => Promise<void>;
}

const AddressContext = createContext<AddressContextType | null>(null);

export const AddressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const refreshAddresses = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoading(true);
    try {
      const user = await userRepository.getUserById(userId);
      if (user) {
        setAddresses(user.addresses || []);
      }
    } catch (error) {
      logger.error('[AddressContext] Error refreshing addresses', error instanceof Error ? error : new Error(String(error)));
    } finally {
      setLoading(false);
    }
  }, []);

  const addAddress = useCallback(async (address: Omit<Address, 'id'>): Promise<boolean> => {
    try {
      if (!user?.id) {
        logger.error('[AddressContext] Cannot add address: no user logged in', new Error('No user logged in'));
        return false;
      }
      logger.info('[AddressContext] Adding address');
      await userRepository.addAddress(user.id, address as Address);
      return true;
    } catch (error) {
      logger.error('[AddressContext] Error adding address', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [user?.id]);

  const removeAddress = useCallback(async (address: Address): Promise<boolean> => {
    try {
      if (!user?.id) {
        logger.error('[AddressContext] Cannot remove address: no user logged in', new Error('No user logged in'));
        return false;
      }
      logger.info(`[AddressContext] Removing address ${address.id}`);
      await userRepository.removeAddress(user.id, address);
      return true;
    } catch (error) {
      logger.error('[AddressContext] Error removing address', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [user?.id]);

  const setDefaultAddress = useCallback(async (address: Address): Promise<boolean> => {
    try {
      if (!user?.id) {
        logger.error('[AddressContext] Cannot set default address: no user logged in', new Error('No user logged in'));
        return false;
      }
      logger.info(`[AddressContext] Setting default address ${address.id}`);
      // Move default address to first position
      const updatedAddresses = addresses.filter(a => a.id !== address.id);
      updatedAddresses.unshift(address);
      await userRepository.updateAddresses(user.id, updatedAddresses);
      setAddresses(updatedAddresses);
      return true;
    } catch (error) {
      logger.error('[AddressContext] Error setting default address', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [addresses, user?.id]);

  const getDefaultAddress = useCallback((): Address | undefined => {
    return addresses.find(a => a.isDefault) || addresses[0];
  }, [addresses]);

  const value = useMemo(() => ({
    addresses,
    loading,
    addAddress,
    removeAddress,
    setDefaultAddress,
    getDefaultAddress,
    refreshAddresses
  }), [addresses, loading, addAddress, removeAddress, setDefaultAddress, getDefaultAddress, refreshAddresses]);

  return (
    <AddressContext.Provider value={value}>
      {children}
    </AddressContext.Provider>
  );
};

export const useAddresses = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddresses must be used within an AddressProvider');
  }
  return context;
};

export default AddressContext;
